import { adminFetch, adminFetchRaw } from './dataProvider';

export type ExportMode = 'all' | 'net_new' | 'updated_since';
export type ExportFormat = 'csv' | 'xlsx';

export interface ExportWatermark {
  user_oid:         string;
  conference_id:    string;
  last_exported_at: string;
  updated_at:       string;
}

/**
 * Explicit "mark as exported" — advances the (caller, conference)
 * watermark to now() without producing a download.
 */
export async function markExported(conferenceId: string): Promise<ExportWatermark> {
  return adminFetch<ExportWatermark>(
    `/v1/admin/conferences/${conferenceId}/export/mark`,
    { method: 'POST', body: '{}' },
  );
}

/**
 * Reads a filename from the Content-Disposition header. Falls back to
 * a generic name when the server didn't set one (the backend always
 * does for csv/xlsx, but this guard keeps the SPA from saving as
 * "(undefined)" if the contract slips).
 */
function readFilename(response: Response, fallback: string): string {
  const cd = response.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

/**
 * Downloads /leads/export with the given mode + format. The backend
 * streams CSV/xlsx with a Content-Disposition filename header; we
 * read the stream into a blob and trigger a browser download.
 *
 * Auto-watermark: every successful download advances the (caller,
 * conferenceId) watermark server-side. "Download = mark exported"
 * by default; the explicit markExported() button is only needed when
 * the user wants to claim records without a download.
 *
 * `selection` is the optional column selection. Each param is appended
 * ONLY when it differs from the server-side default (no hidden columns,
 * discovery + discovery summary both included), so an untouched export
 * dialog produces byte-identical params to the pre-column-picker request.
 * These params are CSV-only — the backend 422s them on the JSON branch.
 */
export async function downloadExport(
  conferenceId: string,
  mode: ExportMode,
  format: ExportFormat,
  selection?: {
    hideColumns?: string[];
    includeDiscovery?: boolean;
    includeDiscoverySummary?: boolean;
  },
): Promise<void> {
  const params = new URLSearchParams({
    conferenceId,
    mode,
    format,
  });
  if (selection?.hideColumns?.length) {
    params.set('hideColumns', selection.hideColumns.join(','));
  }
  if (selection?.includeDiscovery === false) {
    params.set('includeDiscovery', 'false');
  }
  if (selection?.includeDiscoverySummary === false) {
    params.set('includeDiscoverySummary', 'false');
  }

  const response = await adminFetchRaw(
    `/v1/admin/leads/export?${params.toString()}`,
  );
  const blob = await response.blob();
  const filename = readFilename(response, `leads_export.${format}`);

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Wire shape of GET/PUT /v1/admin/export-preferences. */
export interface ExportColumnPrefsWire {
  availableColumns: Array<{ key: string; header: string }>;
  hiddenColumns: string[];
  includeDiscovery: boolean;
  includeDiscoverySummary: boolean;
}

/** The export column catalog plus this user's saved selection. */
export function getColumnPrefs(): Promise<ExportColumnPrefsWire> {
  return adminFetch<ExportColumnPrefsWire>('/v1/admin/export-preferences');
}

/**
 * Persists the column selection. `adminFetch` sends
 * `Content-Type: application/json` via authHeaders(), so the PUT needs
 * no explicit header of its own.
 */
export function saveColumnPrefs(prefs: {
  hiddenColumns: string[];
  includeDiscovery: boolean;
  includeDiscoverySummary: boolean;
}): Promise<void> {
  return adminFetch<void>('/v1/admin/export-preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}
